import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import {
  Bot,
  Send,
  Loader2,
  User,
  Sparkles,
  Trash2,
  X,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIChat = ({ onClose, isExpanded, onToggleExpand }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Add initial greeting
    if (messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          content:
            "Hello! I'm your AI Stock Controller. I can help you with:\n\n• Analyzing inventory levels\n• Suggesting reorders for low stock items\n• Answering questions about your stock\n• Recommending suppliers\n\nHow can I help you today?",
        },
      ]);
    }
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai/chat`,
        { message: userMessage, session_id: sessionId },
        { withCredentials: true }
      );

      setSessionId(response.data.session_id);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);
    } catch (error) {
      console.error("AI chat error:", error);
      const errorMsg =
        error.response?.data?.detail || "Failed to get AI response";
      toast.error(errorMsg);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Sorry, I encountered an error: ${errorMsg}`,
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = async () => {
    try {
      if (sessionId) {
        await axios.delete(`${API}/ai/chat/history?session_id=${sessionId}`, {
          withCredentials: true,
        });
      }
      setMessages([
        {
          role: "assistant",
          content: "Chat cleared. How can I help you?",
        },
      ]);
      setSessionId(null);
    } catch (error) {
      console.error("Error clearing chat:", error);
    }
  };

  return (
    <Card
      className={`rounded-sm border-slate-200 shadow-lg flex flex-col ${
        isExpanded ? "fixed inset-4 z-50" : "h-[500px]"
      }`}
      data-testid="ai-chat-widget"
    >
      <CardHeader className="pb-2 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
              <Bot className="h-4 w-4 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-base">AI Stock Controller</CardTitle>
              <p className="text-xs text-slate-500">Powered by GPT-5.2</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={clearChat}
              title="Clear chat"
            >
              <Trash2 className="h-4 w-4 text-slate-400" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onToggleExpand}
              title={isExpanded ? "Minimize" : "Expand"}
            >
              {isExpanded ? (
                <Minimize2 className="h-4 w-4 text-slate-400" />
              ) : (
                <Maximize2 className="h-4 w-4 text-slate-400" />
              )}
            </Button>
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={onClose}
              >
                <X className="h-4 w-4 text-slate-400" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        <ScrollArea
          className="flex-1 p-4"
          ref={scrollRef}
        >
          <div className="space-y-4">
            {messages.map((msg, index) => (
              <div
                key={msg.id || `msg-${msg.timestamp || index}`}
                className={`flex gap-3 ${
                  msg.role === "user" ? "flex-row-reverse" : ""
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === "user"
                      ? "bg-slate-900"
                      : "bg-indigo-100"
                  }`}
                >
                  {msg.role === "user" ? (
                    <User className="h-4 w-4 text-white" />
                  ) : (
                    <Sparkles className="h-4 w-4 text-indigo-600" />
                  )}
                </div>
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    msg.role === "user"
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-900"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                </div>
                <div className="bg-slate-100 rounded-lg p-3">
                  <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about inventory, stock levels, or suppliers..."
              className="rounded-sm"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="rounded-sm bg-indigo-600 hover:bg-indigo-700"
              data-testid="ai-chat-send"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIChat;
